/**
 * Standardized Timestamp Handling Utilities
 *
 * This composable provides consistent timestamp parsing and formatting
 * across all components, eliminating timezone-related bugs.
 */

import { Timestamp } from 'firebase/firestore';
import { FRONTEND_BUSINESS_RULES } from '@/config/appConfig';
import { useDebugTimeStore } from '@/stores/debugTime';
import {
  formatRelativeDays,
  formatShortDateWithWeekday,
  noteStamp,
} from '@/utils/taipeiDateLabels';

// 課務總覽 (Hub UI) list labels — Taipei calendar days, pure (callers pass nowMs).
export { formatRelativeDays, formatShortDateWithWeekday, noteStamp };

// Standard timestamp interface for all UI components
export interface StandardTimestamp {
  iso: string; // ISO 8601 string for storage/API
  display: string; // User-friendly display format
  relative: string; // Relative time (e.g., "2 hours ago")
  timestamp: Timestamp; // Original Firebase timestamp
}

// Timezone-aware date parsing following CORE-DEVELOPMENT-PROTOCOLS

function getNow(): Date {
  // Centralized function to get the current time, respecting the debug store.
  // This is the ONLY place that should decide whether to use real or debug time.
  const debugTimeStore = useDebugTimeStore();
  return debugTimeStore.getCurrentTime();
}

export function parseTimestampSafely(input: any): StandardTimestamp | null {
  if (!input) return null;

  let date: Date;

  try {
    // Handle Firebase Timestamps (raw and proxied)
    if (input && typeof input.toDate === 'function') {
      date = input.toDate();
    } else if (input && typeof input.seconds === 'number') {
      date = new Date(input.seconds * 1000);
    } else if (input && typeof input._seconds === 'number') {
      date = new Date(input._seconds * 1000);
    } else if (input instanceof Date) {
      date = input;
    } else if (typeof input === 'string' || typeof input === 'number') {
      date = new Date(input);
    } else {
      return null;
    }

    if (isNaN(date.getTime())) return null;

    return {
      iso: date.toISOString(),
      display: formatDisplayTime(date),
      relative: formatRelativeTime(date),
      timestamp: Timestamp.fromDate(date),
    };
  } catch (error) {
    console.error('Timestamp parsing error:', error, input);
    return null;
  }
}

// Create timezone-aware date from Taipei time components
export function createTaipeiDate(dateInput: string | Timestamp | Date, timeString: string): Date {
  let dateString: string;

  // ✅ ENTERPRISE STANDARDS: Robust type checking to handle mixed data formats
  if (typeof dateInput === 'string') {
    dateString = dateInput;
  } else if (dateInput && typeof (dateInput as any).toDate === 'function') {
    // Handle Firestore Timestamp - use type assertion after runtime check
    const timestamp = dateInput as Timestamp;
    const date = timestamp.toDate();
    dateString = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
  } else if (dateInput instanceof Date) {
    // Handle JavaScript Date
    dateString = `${dateInput.getFullYear()}/${String(dateInput.getMonth() + 1).padStart(2, '0')}/${String(dateInput.getDate()).padStart(2, '0')}`;
  } else {
    // ✅ ENTERPRISE STANDARDS: Proper error handling for invalid inputs
    console.error('createTaipeiDate received invalid dateInput:', dateInput);
    throw new Error(
      `Invalid dateInput type: expected string, Timestamp, or Date, got ${typeof dateInput}`,
    );
  }

  const formattedDate = dateString.replace(/\//g, '-');
  const isoString = `${formattedDate}T${timeString}:00+08:00`;
  return new Date(isoString);
}

function formatDisplayTime(date: Date): string {
  const now = getNow();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  // Get the start of the day for 'now' and 'date' in the local timezone
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (startOfDate.getTime() === startOfToday.getTime()) {
    return `今天 ${hours}:${minutes}`;
  } else if (startOfDate.getTime() === startOfYesterday.getTime()) {
    return `昨天 ${hours}:${minutes}`;
  } else {
    const diffMs = now.getTime() - date.getTime();
    // Only calculate diffDays if it's not today or yesterday
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays >= 0 && diffDays < 7) {
      return `${diffDays}天前 ${hours}:${minutes}`;
    } else {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}/${day} ${hours}:${minutes}`;
    }
  }
}

function formatRelativeTime(date: Date): string {
  const now = getNow();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) return '剛剛';
  if (diffMinutes < 60) return `${diffMinutes}分鐘前`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}小時前`;
  return `${Math.floor(diffMinutes / 1440)}天前`;
}

// 2026-09-20 (browser-freeze M1, audit HIGH-1): `new Intl.DateTimeFormat`
// resolves locale + timezone data on EVERY construction (~50 µs measured) while
// a `.format()` call costs ~0.6 µs. These two helpers sit under the hottest
// render paths in the app (the 候補 per-week lines reach them ~55× per row per
// render), so the formatter is built once and reused. Created lazily so module
// evaluation order can never matter. Output is byte-identical to the previous
// per-call construction — proven by useStandardizedTimestamps.test.ts.
let taipeiYmdFormatter: Intl.DateTimeFormat | null = null;
function taipeiYmd(): Intl.DateTimeFormat {
  if (!taipeiYmdFormatter) {
    taipeiYmdFormatter = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: FRONTEND_BUSINESS_RULES.DATE_TIME.TIMEZONE,
    });
  }
  return taipeiYmdFormatter;
}

// New function to format a date as YYYY/MM/DD in Taipei timezone
export function formatDateForDisplay(date: Date): string {
  // Intl.DateTimeFormat is the robust, timezone-aware way to do this on the
  // client — one shared instance (see taipeiYmd above).
  return taipeiYmd().format(date).replace(/-/g, '/');
}

// New function to format a date as YYYY-MM-DD for API calls
export function formatDateForApi(date: Date): string {
  return taipeiYmd().format(date);
}

/**
 * Short Taipei calendar date of a stored instant: 「5/16」 within the current
 * Taipei year, 「2025/5/16」 otherwise — a list sorted oldest-first that spans a
 * new year must not read as out of order. '' when the input is missing or
 * unparseable. Accepts Timestamp / {_seconds} / Date / ms like
 * getDateFromTimestamp. Calendar day and "current year" are both Taipei
 * (formatDateForApi), never the browser's timezone.
 */
export function formatShortTaipeiDate(input: unknown): string {
  const date = getDateFromTimestamp(input);
  if (!date) return '';
  const [y, m, d] = formatDateForApi(date).split('-');
  const md = `${Number(m)}/${Number(d)}`;
  const currentYear = formatDateForApi(getNow()).slice(0, 4);
  return y === currentYear ? md : `${y}/${md}`;
}

/**
 * The calendar day the user PICKED in an n-date-picker, as YYYY-MM-DD.
 *
 * A date picker hands back the epoch-ms of local midnight (or 23:59:59 for a
 * range end) in the BROWSER's timezone. Running that instant through
 * `formatDateForApi` re-reads it in Asia/Taipei, which is a different day for
 * a user west of Taipei: 8/31 23:59:59 in Los Angeles is 9/1 14:59 in Taipei,
 * so a range ending 8/31 was sent as 9/1 (PO 2026-09-08). The label the user
 * clicked is the business date; read it back with the same local calendar the
 * picker rendered it in. Use this for picker values ONLY — for "now" or a
 * stored instant, `formatDateForApi` (Taipei) is still the right call.
 */
export function formatPickerDate(pickerMs: number): string {
  const d = new Date(pickerMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatHistoryTimestamp(timestamp: any): string {
  try {
    const parsedTimestamp = parseTimestampSafely(timestamp);
    if (!parsedTimestamp) return '無效時間';

    return parsedTimestamp.display;
  } catch (error) {
    return '無效時間';
  }
}

export function getDateFromTimestamp(input: any): Date | null {
  if (!input) return null;
  try {
    if (input && typeof input.toDate === 'function') {
      return input.toDate();
    } else if (input && typeof input.seconds === 'number') {
      return new Date(input.seconds * 1000);
    } else if (input && typeof input._seconds === 'number') {
      return new Date(input._seconds * 1000);
    } else if (input instanceof Date) {
      return input;
    } else if (typeof input === 'string' || typeof input === 'number') {
      const date = new Date(input);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    return null;
  } catch (error) {
    console.error('Error converting timestamp to Date:', error, input);
    return null;
  }
}

export function formatAsTime(input: any): string {
  const date = getDateFromTimestamp(input);
  if (!date) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function formatAsTimeWithSeconds(input: any): string {
  const date = getDateFromTimestamp(input);
  if (!date) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

export function formatToDayOfWeek(input: any): string {
  const date = getDateFromTimestamp(input);
  if (!date) return '';
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayOfWeek = FRONTEND_BUSINESS_RULES.DATE_TIME.WEEKDAY_NAMES[date.getDay()];
  return `${month}/${day} ${dayOfWeek}`;
}

export function formatUpdateTimestamp(input: any): string {
  const date = getDateFromTimestamp(input);
  if (!date) return '';
  try {
    const M = String(date.getMonth() + 1).padStart(2, '0');
    const D = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${M}/${D} ${h}:${m}:${s}`;
  } catch (e) {
    console.error('Error formatting update timestamp:', e);
    return '';
  }
}

/**
 * Format a Firestore timestamp that may arrive in multiple serialization formats.
 * Handles: Timestamp objects (with .toDate()), serialized {_seconds} objects,
 * plain strings, and plain numbers (unix ms).
 *
 * @param ts   - The raw timestamp value from Firestore / Cloud Function response
 * @param style - 'date' renders YYYY/MM/DD; 'datetime' renders YYYY/MM/DD HH:MM (default)
 */
export function formatFirestoreTimestamp(
  ts: unknown,
  style: 'date' | 'datetime' = 'datetime',
): string {
  if (!ts) return '';
  try {
    let date: Date;
    if (
      typeof ts === 'object' &&
      ts !== null &&
      'toDate' in ts &&
      typeof (ts as { toDate: () => Date }).toDate === 'function'
    ) {
      date = (ts as { toDate: () => Date }).toDate();
    } else if (typeof ts === 'object' && ts !== null && '_seconds' in ts) {
      date = new Date((ts as { _seconds: number })._seconds * 1000);
    } else if (typeof ts === 'string' || typeof ts === 'number') {
      date = new Date(ts);
    } else {
      return '';
    }

    if (isNaN(date.getTime())) return '';

    const options: Intl.DateTimeFormatOptions =
      style === 'date'
        ? {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: FRONTEND_BUSINESS_RULES.DATE_TIME.TIMEZONE,
          }
        : {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: FRONTEND_BUSINESS_RULES.DATE_TIME.TIMEZONE,
          };

    return date.toLocaleDateString('zh-TW', options);
  } catch {
    return '';
  }
}

/**
 * Hour of day (0–23) of `date` in Asia/Taipei, whatever the device's zone.
 * (formatAsTime et al. read the device-local hour.) Used by warm-on-use.
 */
export function getTaipeiHour(date: Date): number {
  const hour = new Intl.DateTimeFormat('en-GB', {
    timeZone: FRONTEND_BUSINESS_RULES.DATE_TIME.TIMEZONE,
    hour: '2-digit',
    hourCycle: 'h23',
  }).format(date);
  return Number(hour);
}

let taipeiHmFormatter: Intl.DateTimeFormat | null = null;

/**
 * 24-hour HH:MM of an instant in Asia/Taipei, whatever the device's zone
 * (formatFirestoreTimestamp's 'datetime' renders 12-hour 上午/下午 in zh-TW).
 * One formatter for the app — callers build row values in loops.
 */
export function formatTaipeiHm(ms: number): string {
  if (!taipeiHmFormatter) {
    taipeiHmFormatter = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: FRONTEND_BUSINESS_RULES.DATE_TIME.TIMEZONE,
    });
  }
  return taipeiHmFormatter.format(new Date(ms));
}

export function useStandardizedTimestamps() {
  return {
    getNow,
    getTaipeiHour,
    parseTimestampSafely,
    formatUpdateTimestamp,
    formatAsTime,
    formatAsTimeWithSeconds,
    getDateFromTimestamp,
    createTaipeiDate,
    formatDateForDisplay,
    formatDateForApi,
    formatShortTaipeiDate,
    formatPickerDate,
    formatHistoryTimestamp,
    formatToDayOfWeek,
    formatFirestoreTimestamp,
    formatRelativeDays,
    formatShortDateWithWeekday,
    noteStamp,
  };
}
